// src/pages/admin/AdminCreateSDGs.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  FaFileImage,
  FaSpinner,
  FaTrash,
  FaSave,
  FaTimes,
} from "react-icons/fa";

const FormSkeleton = () => {
  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-12 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded" />
        <div className="h-44 bg-gray-200 rounded" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
        </div>
        <div className="h-12 bg-gray-200 rounded w-1/2" />
      </div>

      <div className="w-96 space-y-4">
        <div className="h-48 bg-gray-200 rounded" />
        <div className="h-6 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-200 rounded" />
      </div>
    </div>
  );
};

const SDGPreviewCard = ({ sdg }) => {
  const [formattedTitle, setFormattedTitle] = useState("");
  const title = sdg?.title || "";
  const bgColor = sdg?.bgColor || "#AD3D42";
  const icon = sdg?.icon || "";
  const index = sdg?.sdgNumber || 0;

  useEffect(() => {
    if (title) {
      const words = title.split(" ");

      if (words.length <= 2) {
        const firstLine = words[0] || "";
        const secondLine = words[1] || "\u200B";
        setFormattedTitle(`${firstLine}\n${secondLine}`);
      } else {
        const mid = Math.ceil(words.length / 2);
        const firstLine = words.slice(0, mid).join(" ");
        const secondLine = words.slice(mid).join(" ");
        setFormattedTitle(`${firstLine}\n${secondLine}`);
      }
    }
  }, [title]);

  return (
    <div
      style={{ backgroundColor: bgColor }}
      className={`flex flex-col items-center ${
        title ? "justify-around" : "justify-center"
      }  h-[10rem] pl-4 pr-2 py-3 rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-lg rounded-bl-lg text-white transition-all hover:scale-[102%] duration-200`}
    >
      {title && (
        <div className="flex w-full mx-auto items-start">
          <span className="text-3xl md:text-4xl lg:text-5xl xl:text-4xl font-bold text-left flex-shrink-0">
            {index}
          </span>

          <h3
            className="pl-1 m-1 md:mt-2 lg:mt-3 xl:mt-2 font-semibold text-[0.9rem] sm:text-[1rem] md:text-[0.92rem] tracking-wide capitalize text-left w-full whitespace-pre-line leading-tight min-h-[2.8em] flex items-center"
            style={{ whiteSpace: "pre-line" }}
          >
            {formattedTitle}
          </h3>
        </div>
      )}

      {icon ? (
        <img
          src={icon}
          alt={title}
          className={`${title ? "size-16 mt-2" : "size-[5rem]"}`} 
        />
      ) : (
        <span className="text-xs opacity-70">No Icon</span>
      )}
    </div>
  );
};

const AdminCreateSDGs = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({
    sdgNumber: "",
    title: "",
    icon: "",
    bgColor: "#AD3D42",
    sequence: 0,
    isActive: true,
  });

  const [iconPlaceholder, setIconPlaceholder] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);

  const iconRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchForEdit = async () => {
      if (!isEdit) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const resp = await axiosInstance.get(`/sdgs/${id}`);
        const found = resp?.data?.data ?? [];
        if (!found) {
          toast.error("SDG not found");
          navigate(-1);
          return;
        }
        if (!cancelled) {
          setForm({
            sdgNumber: found.sdgNumber ?? "",
            title: found.title ?? "",
            icon: found.icon ?? "",
            bgColor: found.bgColor ?? "#AD3D42",
            sequence: found.sequence ?? 0,
            isActive:
              typeof found.isActive === "boolean" ? found.isActive : true,
            _id: found._id,
          });
          setOriginal({
            sdgNumber: found.sdgNumber ?? "",
            title: found.title ?? "",
            icon: found.icon ?? "",
            bgColor: found.bgColor ?? "#AD3D42",
            sequence: found.sequence ?? 0,
            isActive:
              typeof found.isActive === "boolean" ? found.isActive : true,
            _id: found._id,
          });
        }
      } catch (err) {
        console.error("Failed to fetch sdg for edit:", err);
        toast.error("Failed to load SDG");
        navigate(-1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchForEdit();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const setField = (key, value) =>
    setForm((p) => {
      return { ...p, [key]: value };
    });

  const hasChanges = () => {
    if (!isEdit) {
      // in create mode, return true if any field has non-default content
      return (
        !!form.title ||
        !!form.sdgNumber ||
        !!form.icon ||
        !!form.bgColor ||
        !!form.sequence ||
        form.isActive === false // if toggled to false
      );
    }
    // edit mode: compare with original
    if (!original) return false;
    const a = { ...original };
    const b = { ...form };
    // remove _id if present
    delete a._id;
    delete b._id;
    return JSON.stringify(a) !== JSON.stringify(b);
  };

  const triggerIconInput = () => iconRef.current && iconRef.current?.click();

  const handleIconChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ph = {
      id: `icon_${Date.now()}`,
      preview: URL.createObjectURL(file),
      file,
    };
    setIconPlaceholder(ph);
    setIconUploading(true);

    try {
      const url = await uploadToCloudinary(file);
      if (!url) throw new Error("Upload failed");
      setForm((p) => ({ ...p, icon: url }));
      toast.success("Icon uploaded");
    } catch (err) {
      console.error("Icon upload error:", err);
      toast.error("Icon upload failed");
    } finally {
      setIconPlaceholder(null);
      setIconUploading(false);
      e.target.value = null;
    }
  };

  const removeIcon = () => {
    setForm((p) => ({ ...p, icon: "" }));
  };

  const validate = () => {
    if (!String(form.sdgNumber).trim())
      return toast.error("SDG Number is required");
    if (!String(form.title).trim()) return toast.error("Title is required");
    if (!form.icon) return toast.error("Icon is required");
    if (!String(form.bgColor).trim())
      return toast.error("Background color is required");
    // sequence can be zero, but must be numeric
    if (form.sequence === "" || isNaN(Number(form.sequence)))
      return toast.error("Sequence must be a number");
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      sdgNumber: Number(form.sdgNumber),
      title: String(form.title).trim(),
      icon: String(form.icon).trim(),
      bgColor: String(form.bgColor).trim(),
      sequence: Number(form.sequence) || 0,
      isActive: !!form.isActive,
    };

    try {
      if (isEdit && form._id) {
        const resp = await axiosInstance.put(`/sdgs/${form._id}`, payload);
        const data = resp?.data?.data ?? resp?.data;
        toast.success("SDG updated");
        setOriginal({ ...data });
        setForm((p) => ({ ...p, ...data }));
        navigate(-1);
      } else {
        await axiosInstance.post(`/sdgs`, payload);
        toast.success("SDG created");
        navigate("/admin-sdgs");
      }
    } catch (err) {
      console.error("Save SDG error:", err);
      toast.error(err.response?.data?.message || "Failed to save SDG");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!isEdit || !original) {
      setForm({
        sdgNumber: "",
        title: "",
        icon: "",
        bgColor: "#AD3D42",
        sequence: 0,
        isActive: true,
      });
      setIconPlaceholder(null);
      setIconUploading(false);
      toast.success("Form reset");
      return;
    }

    setForm({ ...original });
    setIconPlaceholder(null);
    setIconUploading(false);
    toast.success("Reset to original");
  };

  const handleCancel = () => {
    if (hasChanges()) {
      Swal.fire({
        title: "Discard changes?",
        text: "You have unsaved changes. Are you sure you want to cancel?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, discard",
      }).then((r) => {
        if (r.isConfirmed) navigate(-1);
      });
      return;
    }
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              {isEdit ? "Edit SDG" : "Create SDG"}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isEdit
                ? "Update SDG details. Preview updates live on the right."
                : "Create a new Sustainable Development Goal and preview it live."}
            </p>
            {hasChanges() && (
              <p className="text-xs text-amber-600 mt-2">
                You have unsaved changes
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>

            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
              disabled={!hasChanges()}
            >
              Reset
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !hasChanges()}
              className="px-4 py-2 rounded-md bg-primaryColor text-white hover:bg-[#022b31] disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
              {isEdit ? "Save Changes" : "Create SDG"}
            </button>
          </div>
        </div>

        {/* Main content: left form, right preview */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* left: form */}
          <div className="md:col-span-2 bg-white rounded-lg p-6 shadow-sm">
            {loading ? (
              <FormSkeleton />
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
                className="space-y-6"
              >
                {/* SDG Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SDG Number *
                  </label>
                  <input
                    type="number"
                    value={form.sdgNumber}
                    onChange={(e) => setField("sdgNumber", e.target.value)}
                    className="mt-2 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                    min={1}
                    max={17}
                    placeholder="Enter SDG number (1 - 17)"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    className="mt-2 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="e.g. No Poverty"
                  />
                </div>

                {/* BG color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Card Background
                  </label>
                  <div className="mt-2 flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full border-2 border-gray-200 shadow-sm cursor-pointer"
                      style={{ backgroundColor: form.bgColor }}
                      onClick={() => {
                        const el = document.getElementById("bg-hex-input");
                        el?.focus();
                      }}
                    />
                    <input
                      type="color"
                      value={form.bgColor}
                      onChange={(e) => setField("bgColor", e.target.value)}
                      className="w-12 h-12 p-0 border-none cursor-pointer"
                      title="Pick color"
                    />
                    <input
                      id="bg-hex-input"
                      value={form.bgColor}
                      onChange={(e) => setField("bgColor", e.target.value)}
                      className="px-3 py-2 border rounded-md w-36"
                    />
                    <p className="text-sm text-gray-500">
                      Tip: pick a color close to UN SDG palette
                    </p>
                  </div>
                </div>

                {/* Icon upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Card Icon *
                  </label>
                  <div className="mt-2 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={triggerIconInput}
                      className="flex items-center gap-2 px-3 py-2 border border-dashed rounded-md hover:bg-gray-50"
                    >
                      <FaFileImage />
                      {form.icon ? "Change Icon" : "Upload Icon"}
                    </button>

                    <input
                      ref={iconRef}
                      type="file"
                      accept="image/*"
                      onChange={handleIconChange}
                      className="hidden"
                    />

                    <div className="relative w-20 h-20 rounded border-2 border-gray-300 bg-white flex items-center justify-center">
                      {/* uploading overlay */}
                      {iconUploading && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded">
                          <FaSpinner className="animate-spin text-white" />
                        </div>
                      )}

                      {iconPlaceholder ? (
                        <img
                          src={iconPlaceholder.preview}
                          alt="preview"
                          className="object-contain w-full h-full rounded"
                        />
                      ) : form.icon ? (
                        <>
                          <img
                            src={form.icon}
                            alt="icon"
                            className="object-contain w-full h-full rounded"
                          />
                          <button
                            type="button"
                            onClick={removeIcon}
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-700"
                          >
                            <FaTimes size={12} />
                          </button>
                        </>
                      ) : (
                        <div className="text-xs text-gray-400">No icon</div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: 75×75 px. Prefer simple icons with transparent
                    backgrounds.
                  </p>
                </div>

                {/* Sequence & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sequence
                    </label>
                    <input
                      type="number"
                      value={form.sequence}
                      onChange={(e) =>
                        setField("sequence", Number(e.target.value))
                      }
                      className="mt-2 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                      min={0}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers appear first.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      value={String(form.isActive)}
                      onChange={(e) =>
                        setField("isActive", e.target.value === "true")
                      }
                      className="mt-2 w-full px-3 py-2 border rounded-md"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Save area (bottom) */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={!hasChanges()}
                    className="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !hasChanges()}
                    className="px-4 py-2 rounded-md bg-primaryColor text-white hover:bg-[#022b31] disabled:opacity-60 flex items-center gap-2"
                  >
                    {saving ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <FaSave />
                    )}
                    {isEdit ? "Save Changes" : "Create SDG"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* right: preview */}
          <div className="sticky top-0 md:col-span-1 bg-white rounded-lg p-6 shadow-sm">
            {loading ? (
              <div className="space-y-4">
                <div className="h-48 bg-gray-200 rounded" />
                <div className="h-6 bg-gray-200 rounded w-1/2" />
                <div className="h-32 bg-gray-200 rounded" />
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">
                    Live Preview
                  </h3>
                  <div className="text-xs text-gray-500">
                    Public view preview
                  </div>
                </div>

                <SDGPreviewCard
                  sdg={{
                    sdgNumber: form.sdgNumber || 0,
                    title: form.title || "Title",
                    icon: form.icon || "",
                    bgColor: form.bgColor || "#AD3D42",
                  }}
                />

                <div className="mt-6 space-y-2">
                  <div className="text-sm text-gray-700">Preview Details</div>
                  <div className="text-xs text-gray-500">
                    SDG Number:{" "}
                    <span className="font-medium">{form.sdgNumber || "-"}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Sequence:{" "}
                    <span className="font-medium">{form.sequence ?? 0}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Status:{" "}
                    <span className="font-medium">
                      {form.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateSDGs;
